import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface OvertimeRateType {
  id: number;
  name: string;
  multiplier: string;
  approval_levels: number;
}

interface OvertimeRateTypeForm {
  name: string;
  multiplier: string;
  approval_levels: string;
}

const emptyForm: OvertimeRateTypeForm = { name: '', multiplier: '', approval_levels: '1' };

export function OvertimeRateTypes() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<OvertimeRateType[] | null>(null);
  const [editing, setEditing] = useState<'new' | OvertimeRateType | null>(null);
  const [form, setForm] = useState<OvertimeRateTypeForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<OvertimeRateType[]>('/overtime-rate-types').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: OvertimeRateType) => {
    setForm({ name: item.name, multiplier: item.multiplier, approval_levels: String(item.approval_levels) });
    setErrors({});
    setEditing(item);
  };

  const set = (key: keyof OvertimeRateTypeForm) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/overtime-rate-types', form);
      else if (editing) await api.put(`/overtime-rate-types/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: OvertimeRateType) => {
    if (!(await confirmDelete(`Delete "${item.name}"?`))) return;
    try {
      await api.delete(`/overtime-rate-types/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Overtime Rate Types</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Multiplier</th>
                <th>Approval Levels</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td className="muted">{item.multiplier}x</td>
                  <td className="muted">{item.approval_levels === 0 ? 'None (auto-approved)' : item.approval_levels}</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted center">
                    No overtime rate types yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add overtime rate type' : 'Edit overtime rate type'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <input value={form.name} onChange={set('name')} required />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label className="span-2">
              Multiplier *
              <input type="number" step="0.01" min="0.01" value={form.multiplier} onChange={set('multiplier')} required />
              {errors.multiplier && <small className="error">{errors.multiplier[0]}</small>}
            </label>
            <label className="span-2">
              Workflow Approval Levels *
              <input type="number" step="1" min="0" max="10" value={form.approval_levels} onChange={set('approval_levels')} required />
              <small className="muted">
                How many managers must approve, in order — 1: manager, 2: manager's manager, etc. 0 means no approval is
                required; the request is approved automatically.
              </small>
              {errors.approval_levels && <small className="error">{errors.approval_levels[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
